/* Copyright (c) 2026 Dobler Data Solutions, LLC. All rights reserved. */

import { create } from 'zustand';
import type { FormulaDetailDto, TaxonomyCodeDto } from '@shared/types';
import { fetchFormulaDetail, searchTaxonomy, assignTaxonomy, removeAssignment, fetchNextUnassigned, batchAssign } from '../services/formularyApi';
import { trackEvent } from '../services/telemetry';

interface AssignmentState {
  formula: FormulaDetailDto | null;
  isLoading: boolean;
  error: string | null;
  isSaving: boolean;
  saveError: string | null;
  searchResults: TaxonomyCodeDto[];
  searchQuery: string;
  isSearching: boolean;
  successMessage: string | null;

  loadFormula: (setId: string) => Promise<void>;
  searchTaxonomy: (q: string) => Promise<void>;
  assign: (formulaSetId: string, taxonomyCodeSetId: string, notes?: string) => Promise<void>;
  batchAssignTaxonomies: (formulaSetId: string, taxonomyCodeSetIds: string[]) => Promise<void>;
  reassign: (xlinkSetId: string, newTaxonomyCodeSetId: string) => Promise<void>;
  remove: (xlinkSetId: string, reason: string) => Promise<void>;
  advanceToNext: (currentName: string) => Promise<string | null>;
  clearSuccess: () => void;
}

export const useAssignmentStore = create<AssignmentState>((set, get) => ({
  formula: null,
  isLoading: false,
  error: null,
  isSaving: false,
  saveError: null,
  searchResults: [],
  searchQuery: '',
  isSearching: false,
  successMessage: null,

  loadFormula: async (setId: string) => {
    set({ isLoading: true, error: null, successMessage: null });
    try {
      const response = await fetchFormulaDetail(setId);
      set({ formula: response.data, isLoading: false });
    } catch (err) {
      set({ isLoading: false, error: (err as Error).message });
    }
  },

  searchTaxonomy: async (q: string) => {
    set({ searchQuery: q });
    if (q.length < 1) {
      set({ searchResults: [], isSearching: false });
      return;
    }
    set({ isSearching: true });
    try {
      const response = await searchTaxonomy(q);
      set({ searchResults: response.data, isSearching: false });
    } catch {
      set({ isSearching: false });
    }
  },

  assign: async (formulaSetId, taxonomyCodeSetId, notes) => {
    set({ isSaving: true, saveError: null });
    try {
      await assignTaxonomy({ formulaSetId, taxonomyCodeSetId, notes });
      set({ isSaving: false, successMessage: 'Taxonomy assigned successfully', searchResults: [], searchQuery: '' });
      trackEvent('taxonomy_assigned');
      await get().loadFormula(formulaSetId);
    } catch (err) {
      set({ isSaving: false, saveError: (err as Error).message });
    }
  },

  batchAssignTaxonomies: async (formulaSetId, taxonomyCodeSetIds) => {
    set({ isSaving: true, saveError: null });
    try {
      const res = await batchAssign({ formulaSetIds: [formulaSetId], taxonomyCodeSetIds });
      const { successCount, duplicateCount, failureCount, failures } = res.data;
      const parts: string[] = [];
      if (successCount > 0) parts.push(`${successCount} assigned`);
      if (duplicateCount > 0) parts.push(`${duplicateCount} already assigned`);
      if (failureCount > 0) parts.push(`${failureCount} failed`);
      const summary = parts.join(', ') || 'No changes';
      if (failureCount > 0) {
        set({ isSaving: false, saveError: `${summary}. Failures: ${failures.map((f) => f.error).join('; ')}` });
      } else {
        set({ isSaving: false, successMessage: summary });
      }
      await get().loadFormula(formulaSetId);
    } catch (err) {
      set({ isSaving: false, saveError: (err as Error).message });
    }
  },

  remove: async (xlinkSetId, reason) => {
    set({ isSaving: true, saveError: null });
    try {
      await removeAssignment(xlinkSetId, reason);
      set({ isSaving: false, successMessage: 'Assignment removed' });
      const formula = get().formula;
      if (formula) await get().loadFormula(formula.setId);
    } catch (err) {
      set({ isSaving: false, saveError: (err as Error).message });
    }
  },

  reassign: async (xlinkSetId, newTaxonomyCodeSetId) => {
    set({ isSaving: true, saveError: null });
    try {
      const formula = get().formula;
      if (!formula) throw new Error('No formula loaded');
      await removeAssignment(xlinkSetId, 'Reassigned to different taxonomy code');
      await assignTaxonomy({ formulaSetId: formula.setId, taxonomyCodeSetId: newTaxonomyCodeSetId, notes: 'Reassignment' });
      set({ isSaving: false, successMessage: 'Taxonomy reassigned successfully', searchResults: [], searchQuery: '' });
      await get().loadFormula(formula.setId);
    } catch (err) {
      set({ isSaving: false, saveError: (err as Error).message });
      const formula = get().formula;
      if (formula) await get().loadFormula(formula.setId);
    }
  },

  advanceToNext: async (currentName: string) => {
    try {
      const response = await fetchNextUnassigned(currentName);
      if (response.meta?.exhausted || !response.data) return null;
      return response.data.setId;
    } catch {
      return null;
    }
  },

  clearSuccess: () => set({ successMessage: null }),
}));
